<template>
  <div class="min-h-screen flex items-center justify-center">
    <div class="w-full max-w-[420px] p-12 animate-slide-up  bg-white rounded-xl">
      <!-- Header -->
      <div class="flex items-start justify-between mb-5">
        <div class="flex-1">
          <div class="flex items-center gap-2 mb-2">
            <div v-if="currencyConfig.icon" class="w-8 h-8 flex items-center justify-center">
              <img :src="currencyConfig.icon" :alt="currencyConfig.name" class="w-full h-full" />
            </div>
            <div>
              <h1 class="text-2xl font-bold text-vault-text-primary m-0">
                {{ $t('claimView.title') }}
              </h1>
            </div>
          </div>
          <p class="text-xs text-vault-text-secondary m-0">
            {{ $t('claimView.intro') }}
          </p>
        </div>


      </div>

      <!-- Error Display -->
      <div v-if="claimError" class="p-3 bg-red-50 border border-vault-danger-400/30 rounded-xl mb-4">
        <div class="flex items-start gap-2">
          <span class="text-xl">⚠️</span>
          <div>
            <p class="text-xs font-semibold text-vault-danger-500 m-0">
              {{ claimError }}
            </p>
          </div>
        </div>
      </div>

      <!-- Claim Wallet Component -->
      <ClaimWallet
        v-if="claimUrl && !claimError"
        :claim-url="claimUrl"
        :emoji-pin="emojiPin"
        :currency="currencyConfig"
        @wallet-unlocked="handleWalletUnlocked"
        @wallet-locked="handleWalletLocked"
        @transfer-complete="handleTransferComplete"
      />

      <!-- Loading State -->
      <WalletStatusScreen
        v-else-if="!claimError"
        status="loading"
        :title="$t('claimView.preparing')"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import ClaimWallet from '../components/wallet/ClaimWallet.vue';
import WalletStatusScreen from '../components/wallet/WalletStatusScreen.vue';
import { getCurrencyById, getDefaultCurrency } from '../config/currencies';

const { t: $t } = useI18n();
const route = useRoute();

interface CurrencyConfig {
  id: string;
  name: string;
  code: string;
  symbol: string;
  icon: string | null;
  type: 'native' | 'erc20';
  decimals: number;
  contractAddress?: string;
  contractAddresses?: Record<string, string>;
  networks?: Array<{ networkId: string; contractAddress?: string }>;
  description: string;
}

// Get currency from route params
const currencyParam = computed(() => {
  const param = route.params.currency;
  return typeof param === 'string' ? param : param?.[0] || '';
});

// Get currency config
const currencyConfig = computed<CurrencyConfig>(() => {
  if (currencyParam.value) {
    const currency = getCurrencyById(currencyParam.value);
    if (currency) {
      return currency as CurrencyConfig;
    }
  }
  return getDefaultCurrency() as CurrencyConfig;
});

// Local state
const claimUrl = ref<string>('');
const claimError = ref('');
const emojiPin = ref('');
const mnemonic = ref('');

// Build claim URL from current location
onMounted(() => {
  if (typeof window === 'undefined') return;

  const hash = window.location.hash ? window.location.hash.slice(1) : '';
  if (!hash) {
    claimError.value = $t('claimView.errors.noData');
    return;
  }

  // Construct full claim URL
  claimUrl.value = `${window.location.origin}${window.location.pathname}#${hash}`;
});

// Event handlers
function handleWalletUnlocked(data: { mnemonic: string; metadata: any; emojiPin: string }) {
  console.log('🔓 [ClaimView] Wallet unlocked');

  // Store for reference (optional, ClaimWallet component manages its own state)
  mnemonic.value = data.mnemonic;
  emojiPin.value = data.emojiPin;

  // Clear URL hash for security
  if (typeof window !== 'undefined') {
    window.history.replaceState(null, document.title, window.location.pathname);
  }
}

function handleWalletLocked() {
  console.log('🔒 [ClaimView] Wallet locked');

  // Clear local references
  mnemonic.value = '';
  emojiPin.value = '';
}

function handleTransferComplete(result: any) {
  console.log('✅ [ClaimView] Transfer completed:', result);
  // Additional logic can go here if needed (e.g., analytics, notifications)
}
</script>

<style scoped>
/* Animation defined in index.css */
</style>
